import { useCallback, useRef } from "react";
import { FIELD_ACTION_SENDS, type FieldActionCompose } from "@/features/catalog_case_review/shared/model/fieldActionContext";
import { usePublishFieldActionComposer } from "../../shared/context/AgentCapabilityContext";
import { DOCK_FOCUS, type DockFocusTarget } from "./useAgentDock";
import type { AgentConversation } from "./useAgentConversation";

/** What the composer needs of the conversation; narrow so a test can stub it. */
export type FieldActionConversation = Pick<AgentConversation, "setQuestion" | "ask" | "beginFieldAction">;

/**
 * Takes a field action from a row's button into the dock: opens the panel on the prompt box, pins
 * the field for the turn, and either puts the expanded prompt in the composer to edit (`compose`)
 * or sends it at once (`immediate`). Registered with the capability registry while the dock can
 * take one, so the buttons know whether to enable themselves.
 *
 * <p>The conversation is read through a ref: the registered function's identity reaches provider
 * state, so it must not follow the conversation object, which is rebuilt on every dock render.
 */
export function useFieldActionComposer(conversation: FieldActionConversation, openDock: (focus?: DockFocusTarget) => void, available: boolean): FieldActionCompose {
  const latest = useRef(conversation);
  latest.current = conversation;
  const composeFieldAction = useCallback<FieldActionCompose>((context, action) => {
    const { setQuestion, ask, beginFieldAction } = latest.current;
    openDock(DOCK_FOCUS.composer);
    beginFieldAction(context, action.label);
    // The request goes into the box either way: an immediate send that has to wait for a run in
    // flight is then still there, under its chip, for the reviewer to send.
    setQuestion(action.prompt);
    if (action.send === FIELD_ACTION_SENDS.immediate) void ask(action.prompt);
  }, [openDock]);
  usePublishFieldActionComposer(available ? composeFieldAction : null);
  return composeFieldAction;
}
